import Table from "components/Table/Table";
import { useState } from "react";
import { ApiFile } from "utils/useFlowTs";
import { FileUploader } from "../FileUploader";
import { createColumnHelper } from "@tanstack/react-table";

import edit from 'assets/images/icons/edit.svg';
import trashCan from 'assets/images/icons/trashcan.svg';
import download from 'assets/images/icons/download.svg';
import uploadIcon from 'assets/images/icons/upload.svg';

import { downloadFileAsBlob } from "utils/blob";
import { Modal } from "components/Modal/Modal";

import './FileWidget.scss';

type FileWidgetProps = {
    documents?: ApiFile[];
    onAddFile?: (file: ApiFile) => void;
    onUpdateFile?: (file: ApiFile) => void;
    onDeleteFile?: (file: ApiFile) => void;
}

export function FileWidget({ onDeleteFile, onAddFile, onUpdateFile, documents = [] }: FileWidgetProps) {
    const [files, setFiles] = useState<ApiFile[]>([]);
    const [documentModifications, setDocumentModifications] = useState<ApiFile>();
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);

    const columnHelper = createColumnHelper<ApiFile>();

    const tableColumns = [
        columnHelper.accessor('label', {
            header: 'Nom du fichier',
            cell: (info) => info.getValue(),
            size: 1000
        }),
        columnHelper.display({
            header: 'Actions',
            size: 100,
            id: 'actions',

            cell: ({ row: { original: currRow } }) => {
                return (
                    <span style={{ display: 'flex', columnGap: 12, justifyContent: 'end' }}>
                        <img
                            src={edit}
                            alt="Modifier le document"
                            onClick={() => {
                                setDocumentModifications(currRow);
                                setIsDocumentModalOpen(true);
                            }}
                        />
                        <img
                            src={download}
                            alt="Télécharger le document"
                            onClick={() => {
                                downloadFileAsBlob(currRow).catch((error: any) => {
                                    console.error('Erreur lors du téléchargement:', error);
                                });
                            }}
                        />
                        <img
                            src={trashCan}
                            alt="Supprimer le document"
                            onClick={() => {
                                onDeleteFile && onDeleteFile(currRow);
                            }}
                        />
                    </span>
                );
            }
        })
    ];


    return <>
        <div className="file-widget" style={{ width: "100%" }}>
            {documents && documents.length > 0 ? (
                <>
                    <Table data={documents} columns={tableColumns} columnHelper={columnHelper} selectable className="file-widget-table" />
                    <FileUploader
                        files={files}
                        setFiles={setFiles}
                        onSuccess={onAddFile}
                        label="Importer un document supplémentaire"
                        variant="light"
                        icon={<img src={uploadIcon} alt="Importer un document supplémentaire" />}
                    />
                </>
            ) : (
                <FileUploader
                    files={files}
                    setFiles={setFiles}
                    onSuccess={onAddFile}
                    size="big"
                />
            )}
            <p className="accepted-file" style={{ textAlign: "center", width: "100%", fontSize: 16 }}>
                Types de fichiers acceptés : pdf, docx, doc, xlsx, xls, pptx, ppt, txt, csv, jpg, jpeg, png,
                gif.
            </p>
        </div>
        <Modal
            open={isDocumentModalOpen}
            setClose={() => {
                setIsDocumentModalOpen(false);
                setDocumentModifications(undefined);
            }}
            title="Modifier les informations du document"
            yesText="Modifier"
            noText="Annuler"
            onValidate={() => {
                if (documentModifications) {
                    onUpdateFile && onUpdateFile(documentModifications);
                }

                setIsDocumentModalOpen(false);
                setDocumentModifications(undefined);
            }}
            onClose={() => {
                setIsDocumentModalOpen(false);
                setDocumentModifications(undefined);
            }}
        >
        <div className="form">
            <div className="form-line">
                <div className="form-group">
                    <label>Nom du fichier</label>
                    <input
                        type="text"
                        name="label"
                        onChange={(e) => {
                            const newLabel = e.target.value;
                            setDocumentModifications((prev) => {
                                if (prev) {
                                    return {
                                        ...prev,
                                        label: newLabel
                                    };
                                }
                                return prev;
                            });
                        }}
                        value={documentModifications?.label || ''}
                    />
                </div>
            </div>
            <div className="form-line">
                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        name="observation"
                        onChange={(e) => {
                            const newObservation = e.target.value;
                            setDocumentModifications((prev) => {
                                if (prev) {
                                    return {
                                        ...prev,
                                        observation: newObservation
                                    };
                                }
                                return prev;
                            });
                        }}
                        value={documentModifications?.observation || ''}
                        rows={6}
                    />
                </div>
            </div>
            <div className="form-line">
                <div className="form-group">
                    <label>Niveau de confidentialité</label>
                    <div className="radios">
                        <label className="radio">
                            <input
                                type="radio"
                                name="level"
                                onChange={(e) => {
                                    setDocumentModifications((prev) => {
                                        if (prev) {
                                            return {
                                                ...prev,
                                                level: 'public'
                                            };
                                        }
                                        return prev;
                                    });
                                }}
                                checked={documentModifications?.level === 'public'}
                                value="public"
                            />
                            Public
                        </label>
                        <label className="radio">
                            <input
                                type="radio"
                                name="level"
                                onChange={(e) => {
                                    setDocumentModifications((prev) => {
                                        if (prev) {
                                            return {
                                                ...prev,
                                                level: 'private'
                                            };
                                        }
                                        return prev;
                                    });
                                }}
                                checked={documentModifications?.level === 'private'}
                                value="private"
                            />
                            Privé
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
    </>
}
